import { dirhams } from '@/lib/booking/money';

export interface DeskReceiptInput {
  id: string;
  customerName: string;
  vehicleName: string;
  totalPrice: number;
  avanceAmount?: number;
  cautionAmount?: number;
  cautionType?: string;
}

export interface DeskReceipt {
  bookingId: string;
  customerName: string;
  vehicleName: string;
  total: number;
  advance: number;
  deposit: number;
  depositKind: string;
  balanceDue: number;
}

/** Desk receipt for the advance and the deposit. Not a tax invoice. */
export function deskReceipt(booking: DeskReceiptInput): DeskReceipt {
  const total = dirhams(booking.totalPrice);
  const advance = dirhams(booking.avanceAmount ?? 0);
  const deposit = dirhams(booking.cautionAmount ?? 0);
  return {
    bookingId: booking.id,
    customerName: booking.customerName,
    vehicleName: booking.vehicleName,
    total,
    advance,
    deposit,
    depositKind: booking.cautionType?.trim() || 'none',
    balanceDue: dirhams(total - advance),
  };
}

export function deskReceiptDocument(receipt: DeskReceipt, agencyName: string): string {
  const body = receiptLines(receipt)
    .map(([label, amount]) => `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(amount)}</td></tr>`)
    .join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt ${escapeHtml(receipt.bookingId)}</title>
<style>body{font-family:sans-serif;margin:32px;color:#111}h1{font-size:18px}p{color:#444}table{border-collapse:collapse;width:100%;max-width:420px}td{padding:8px 0;border-bottom:1px solid #ddd}td:last-child{text-align:right;font-weight:700}.note{margin-top:16px;font-size:12px}</style></head>
<body><h1>${escapeHtml(agencyName)}</h1><p>Desk receipt · not a tax invoice</p><p>${escapeHtml(receipt.customerName)} · ${escapeHtml(receipt.vehicleName)}<br>${escapeHtml(receipt.bookingId)}</p><table>${body}</table><p class="note">Amounts follow the booking record.</p></body></html>`;
}

export function deskReceiptText(receipt: DeskReceipt, agencyName: string): string {
  const lines = receiptLines(receipt).map(([label, amount]) => `${label} ${amount}`);
  return [
    agencyName,
    'Desk receipt. This is not a tax invoice.',
    `${receipt.customerName} · ${receipt.vehicleName}`,
    receipt.bookingId,
    ...lines,
  ].join('\n');
}

function receiptLines(receipt: DeskReceipt): [string, string][] {
  return [
    ['Total rental', formatDirhams(receipt.total)],
    ['Advance received', formatDirhams(receipt.advance)],
    [`Deposit (${receipt.depositKind})`, formatDirhams(receipt.deposit)],
    ['Balance due at pickup', formatDirhams(receipt.balanceDue)],
  ];
}

function formatDirhams(amount: number): string {
  return `${amount.toFixed(2)} DH`;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    const escaped: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return escaped[char] ?? char;
  });
}
